import React from "react";
import { Navigate, RouteProps } from "react-router-dom";

// auth
const Login = React.lazy(() => import("../pages/auth/Login"));
const ForgetPassword = React.lazy(() => import("../pages/auth/ForgetPassword"));
const ResetPasword = React.lazy(() => import("../pages/auth/ResetPassword"));
const Logout = React.lazy(() => import("../pages/auth/Logout"));

const Maintenance = React.lazy(() => import("../pages/other/Maintenance"));
const Error404 = React.lazy(() => import("../pages/error/Error404"));
const Error500 = React.lazy(() => import("../pages/error/Error500"));

// Theme-migration shell preview (public, no auth) — see layouts/PortalLayout
const PortalLayoutPreview = React.lazy(
  () => import("../pages/portal-layout-preview")
);

const Dashboard = React.lazy(() => import("../pages/dashboard"));
const ServicesDefaults = React.lazy(() => import("../pages/services"));
const ServiceDetail = React.lazy(() => import("../pages/services/detail"));
const ComplianceRequirements = React.lazy(() => import("../pages/compliance-requirements"));
const ServiceActivationRequests = React.lazy(() => import("../pages/service-activation-requests"));
const Customers = React.lazy(() => import("../pages/customers"));
const Roles = React.lazy(() => import("../pages/roles"));
const RoleAddEdit = React.lazy(() => import("../pages/roles/add-edit"));
const ChangePassword = React.lazy(() => import("../pages/account/change-password"));
const Profile = React.lazy(() => import("../pages/account/profile"));
const NotificationPreferences = React.lazy(() => import("../pages/account/notifications"));

const Permissions = React.lazy(() => import("../pages/permissions"));
const PermissionAddEdit = React.lazy(
  () => import("../pages/permissions/add-edit")
);

const InfoGroups = React.lazy(() => import("../pages/info-groups"));
const InfoGroupAddEdit = React.lazy(
  () => import("../pages/info-groups/add-edit")
);

const Allergens = React.lazy(() => import("../pages/allergens"));
const AllergensAddEdit = React.lazy(
  () => import("../pages/allergens/add-edit")
);

const Currencies = React.lazy(() => import("../pages/currencies"));
const CurrenciesAddEdit = React.lazy(
  () => import("../pages/currencies/add-edit")
);

const CarBrands = React.lazy(() => import("../pages/car-brands"));
const CarBrandsAddEdit = React.lazy(
  () => import("../pages/car-brands/add-edit")
);
const InfoTypes = React.lazy(() => import("../pages/info-types"));
const InfoTypeAddEdit = React.lazy(
  () => import("../pages/info-types/add-edit/index")
);
const Infos = React.lazy(() => import("../pages/infos"));
const InfoAddEdit = React.lazy(() => import("../pages/infos/add-edit/index"));

const CarModels = React.lazy(() => import("../pages/car-models"));
const CarModelsAddEdit = React.lazy(
  () => import("../pages/car-models/add-edit")
);

const Countries = React.lazy(() => import("../pages/countries"));
const CountryAddEdit = React.lazy(
  () => import("../pages/countries/add-edit/index")
);

const Cities = React.lazy(() => import("../pages/cities"));
const CityAddEdit = React.lazy(() => import("../pages/cities/add-edit/index"));

const LikeCategories = React.lazy(() => import("../pages/like-categories"));
const LikeCategoryAddEdit = React.lazy(
  () => import("../pages/like-categories/add-edit/index")
);

const Languages = React.lazy(() => import("../pages/languages"));
const LanguagesAddEdit = React.lazy(
  () => import("../pages/languages/add-edit/index")
);

const PaymentMethods = React.lazy(() => import("../pages/payment-methods"));
const PaymentMethodsAddEdit = React.lazy(
  () => import("../pages/payment-methods/add-edit/index")
);

const InfoTypeOptions = React.lazy(() => import("../pages/info-type-options"));
const InfoTypeOptionAddEdit = React.lazy(
  () => import("../pages/info-type-options/add-edit")
);

const TransportationTypes = React.lazy(
  () => import("../pages/transportation-types")
);
const TransportationTypeAddEdit = React.lazy(
  () => import("../pages/transportation-types/add-edit/index")
);

const EventCategories = React.lazy(() => import("../pages/event-categories"));
const EventCategoryAddEdit = React.lazy(
  () => import("../pages/event-categories/add-edit/index")
);

const Likes = React.lazy(() => import("../pages/likes"));
const LikesAddEdit = React.lazy(() => import("../pages/likes/add-edit/index"));

const States = React.lazy(() => import("../pages/states"));
const StateAddEdit = React.lazy(() => import("../pages/states/add-edit"));

const Vendors = React.lazy(() => import("../pages/vendors"));
const VendorAddEdit = React.lazy(() => import("../pages/vendors/add-edit"));
const VendorDetail = React.lazy(() => import("../pages/vendors/vendor-detail"));
const VendorSegmentDetail = React.lazy(() => import("../pages/vendors/vendor-detail/segments/detail"));

const VendorApis = React.lazy(() => import("../pages/vendor-apis"));
const VendorApiAddEdit = React.lazy(
  () => import("../pages/vendor-apis/add-edit")
);
const VendorApiDetail = React.lazy(() => import("../pages/vendor-apis/detail"));

const Users = React.lazy(() => import("../pages/users"));
const UserAddEdit = React.lazy(() => import("../pages/users/add-edit/index"));
const UserLogs = React.lazy(() => import("../pages/user-logs"));
const UserDetail = React.lazy(
  () => import("../pages/users/user-details/index")
);

const UserTypes = React.lazy(() => import("../pages/user-types"));

const Suppliers = React.lazy(() => import("../pages/suppliers"));
const SupplierAddEdit = React.lazy(() => import("../pages/suppliers/add-edit"));
const SupplierDetail = React.lazy(
  () => import("../pages/suppliers/supplier-detail")
);
const WaitingApprovals = React.lazy(() => import("../pages/waiting-approvals"));
const ComplianceDocuments = React.lazy(() => import("../pages/compliance/documents"));
const ComplianceContracts = React.lazy(() => import("../pages/compliance/contracts"));

const Agents = React.lazy(() => import("../pages/agents"));
const AgentDetail = React.lazy(() => import("../pages/agents/detail"));

const AgentGroups = React.lazy(() => import("../pages/agent-groups"));
const AgentGroupsDetailAddEdit = React.lazy(
  () => import("../pages/agent-groups/detail/agents/add-edit/")
);
const AgentGroupsAddEdit = React.lazy(
  () => import("../pages/agent-groups/add-edit")
);
const AgentGroupsDetail = React.lazy(
  () => import("../pages/agent-groups/detail")
);

const VendorInvoices = React.lazy(() => import("../pages/vendor-invoices"));
const VendorInvoicesAddEdit = React.lazy(
  () => import("../pages/vendor-invoices/add-edit")
);
const VendorInvoicesDetail = React.lazy(
  () => import("../pages/vendor-invoices/invoice-detail")
);

const Events = React.lazy(() => import("../pages/events"));
const EventAddEdit = React.lazy(() => import("../pages/events/add-edit"));
const EventAttendees = React.lazy(() => import("../pages/events/attendees"));
const EventView = React.lazy(() => import("../pages/events/detail"));

const Approvals = React.lazy(() => import("../pages/approvals"));
const ApprovalsAddEdit = React.lazy(() => import("../pages/approvals/add-edit"));

const Request = React.lazy(() => import("../pages/request"));
const RequestDetail = React.lazy(() => import("../pages/request/detail"));
const ExclusiveReferrals = React.lazy(() => import("../pages/exclusive-referrals"));
const OfferDetail = React.lazy(() => import("../pages/request/offer-detail"));
const CustomerDetail = React.lazy(() => import("../pages/customers/detail"));
const RequestAddEdit = React.lazy(() => import("../pages/request/add-edit"));

const Tickets = React.lazy(() => import("../pages/ticket"));
const TicketAddEdit = React.lazy(
    () => import("../pages/ticket/add-edit/index")
);
const TicketCategories = React.lazy(() => import("../pages/ticket-categories"));
const TicketCategoryAddEdit = React.lazy(
    () => import("../pages/ticket-categories/add-edit")
);

const MobilePages = React.lazy(() => import("../pages/mobile-pages"));
const MobilePageAddEdit = React.lazy(() => import("../pages/mobile-pages/add-edit"));
const MobilePageDetail = React.lazy(() => import("../pages/mobile-pages/detail"));

const SystemFeatures = React.lazy(() => import("../pages/system-features"));
const StorageHealth = React.lazy(() => import("../pages/storage-health"));
const SystemFeatureAddEdit = React.lazy(() => import("../pages/system-features/add-edit"));
const SystemFeatureDetail = React.lazy(() => import("../pages/system-features/detail"));

const Badges = React.lazy(() => import("../pages/badges"));
const BadgeDetail = React.lazy(() => import("../pages/badges/detail"));

const Tags = React.lazy(() => import("../pages/tags"));
const TagDetail = React.lazy(() => import("../pages/tags/detail"));

const RecommendedContentCategories = React.lazy(() => import("../pages/recommended-content-categories"));
const RecommendedContentCategoryDetail = React.lazy(() => import("../pages/recommended-content-categories/detail"));

const RecommendedContent = React.lazy(() => import("../pages/recommended-content"));
const AIAgentConfigPage = React.lazy(() => import("../pages/ai-agent"));
const DispatchSettingsPage = React.lazy(() => import("../pages/dispatch-settings"));
const SupportAutoReplyPage = React.lazy(() => import("../pages/support-auto-reply"));
const KnowledgeBasePage = React.lazy(() => import("../pages/knowledge-base"));
const AiPoliciesPage = React.lazy(() => import("../pages/ai-policies"));
const AiPromptsPage = React.lazy(() => import("../pages/ai-prompts"));
const OfferApprovalPage = React.lazy(() => import("../pages/offer-approval"));
const SupplierRequestPricingPage = React.lazy(() => import("../pages/supplier-request-pricing"));
const RecommendedContentDetail = React.lazy(() => import("../pages/recommended-content/detail"));
const RecommendedContentAddEdit = React.lazy(() => import("../pages/recommended-content/add-edit-page"));

const ContentComponents = React.lazy(() => import("../pages/content-components"));
const ContentComponentTemplates = React.lazy(() => import("../pages/content-component-templates"));
const FxRates = React.lazy(() => import("../pages/fx-rates/index"));
const PaymentIntents = React.lazy(() => import("../pages/payment-intents/index"));
const FinanceOverview = React.lazy(() => import("../pages/finance/overview/index"));
const Withdrawals = React.lazy(() => import("../pages/finance/withdrawals/index"));
const SupplierStatement = React.lazy(() => import("../pages/finance/statement/index"));
const Commission = React.lazy(() => import("../pages/finance/commission/index"));
const LedgerAccounts = React.lazy(() => import("../pages/finance/accounts/index"));
const LedgerTransactions = React.lazy(() => import("../pages/finance/ledger/index"));
const FinancePeriods = React.lazy(() => import("../pages/finance/periods/index"));
const FinanceSplit = React.lazy(() => import("../pages/finance/split/index"));
const FinanceReferrals = React.lazy(() => import("../pages/finance/referrals/index"));
const Reconciliation = React.lazy(() => import("../pages/finance/reconciliation/index"));
const OfferTransfers = React.lazy(() => import("../pages/offer-transfers/index"));
const NotificationLogs = React.lazy(() => import("../pages/notification-logs/index"));
const NotificationSettings = React.lazy(() => import("../pages/notification-settings/index"));
const PublicAssets = React.lazy(() => import("../pages/public-assets"));

  // Aviation
const Airports = React.lazy(() => import("../pages/aviation/airports/index"));
const JetClasses = React.lazy(() => import("../pages/aviation/jet-classes/index"));
const JetClassDetail = React.lazy(() => import("../pages/aviation/jet-classes/detail/index"));
const Aircrafts = React.lazy(() => import("../pages/aviation/aircrafts/index"));
const AircraftDetail = React.lazy(() => import("../pages/aviation/aircrafts/detail/index"));
const EmptyLegs = React.lazy(() => import("../pages/aviation/empty-legs/index"));
const EmptyLegDetail = React.lazy(() => import("../pages/aviation/empty-legs/detail/index"));
const Experiences = React.lazy(() => import("../pages/experiences/index"));
const ExperienceDetail = React.lazy(() => import("../pages/experiences/detail/index"));
const Yachts = React.lazy(() => import("../pages/yachts/index"));
const Security = React.lazy(() => import("../pages/security"));
const MfaManagement = React.lazy(() => import("../pages/mfa-management"));
const Configuration = React.lazy(() => import("../pages/configuration"));
const MessagingChannels = React.lazy(() => import("../pages/messaging-channels"));
const StorageObjects = React.lazy(() => import("../pages/storage-objects"));
const RedisCache = React.lazy(() => import("../pages/redis-cache"));
const OrphanObjects = React.lazy(() => import("../pages/orphan-objects"));
const YachtDetail = React.lazy(() => import("../pages/yachts/detail/index"));
const YachtTypes = React.lazy(() => import("../pages/yacht-types/index"));
const YachtTypeDetail = React.lazy(() => import("../pages/yacht-types/detail/index"));
const VendorPaymentMethods = React.lazy(() => import("../pages/vendor-payment-methods/index"));
const IntegrationRoot = React.lazy(() => import("../pages/integrations"));
const StagingQueue = React.lazy(() => import("../pages/integrations/staging"));
const StagingDetail = React.lazy(() => import("../pages/integrations/staging/detail"));
const Cars = React.lazy(() => import("../pages/cars/index"));
const HotelsList = React.lazy(() => import("../pages/hotels"));
const RestaurantsList = React.lazy(() => import("../pages/restaurants"));
const HotelView = React.lazy(() => import("../pages/hotels/detail"));
const PendingChangesQueue = React.lazy(() => import("../pages/inventory/pending-changes"));
const SubmissionsQueue = React.lazy(() => import("../pages/inventory/submissions"));
const ModerationPolicyPage = React.lazy(() => import("../pages/inventory/moderation-policy"));
const PromotionsPage = React.lazy(() => import("../pages/promotions"));
const PromotionSettingsPage = React.lazy(() => import("../pages/promotions/settings"));
const CrossSellSettingsPage = React.lazy(() => import("../pages/cross-sell/settings"));
const PromotionDetailPage = React.lazy(() => import("../pages/promotions/detail"));
const HotelEdit = React.lazy(() => import("../pages/hotels/edit"));
const RestaurantView = React.lazy(() => import("../pages/restaurants/detail"));
const RestaurantEdit = React.lazy(() => import("../pages/restaurants/edit"));
const CarDetail = React.lazy(() => import("../pages/cars/detail/index"));

export interface RoutesProps {
  path: RouteProps["path"];
  name?: string;
  element?: RouteProps["element"];
  route?: any;
  exact?: boolean;
  icon?: string;
  header?: string;
  permissions?: string[];
  children?: RoutesProps[];
  // Theme migration (Phase 1): opt a page into the new PortalLayout shell.
  // Omitted/"legacy" → renders in the old Hyper layout, unchanged.
  layout?: "portal" | "legacy";
  // Topbar breadcrumb when layout === "portal" (defaults to [{ label: name }]).
  breadcrumb?: { label: string; to?: string }[];
}

// root routes
const rootRoute: RoutesProps = {
  path: "/",
  exact: true,
  element: <Navigate to="/dashboard" />,
};

// dashboards

const dashboardRoutes: RoutesProps = {
  path: "/dashboard",
  name: "Dashboard",
  icon: "airplay",
  element: <Dashboard />,
  layout: "portal",
  breadcrumb: [{ label: "Dashboard" }],
};

const authenticationRoutes: RoutesProps = {
  path: "/authentication",
  name: "Authentication",
  icon: "lock",
  element: <Navigate to="/authentication/roles" />,
  children: [
    {
      // Self-service password change — any authenticated admin (no permission gate).
      path: "/account/change-password",
      name: "Change Password",
      element: <ChangePassword />,
      layout: "portal",
      breadcrumb: [{ label: "Account" }, { label: "Change Password" }],
    },
    {
      // Self-service account overview, reached from the sidebar footer.
      path: "/account/profile",
      name: "Profile",
      element: <Profile />,
      layout: "portal",
      breadcrumb: [{ label: "Account" }, { label: "Profile" }],
    },
    {
      // Each admin chooses their own supplier-event notifications (portal + email).
      // No permission: the page lists only the groups the admin's notify keys allow.
      path: "/account/notifications",
      name: "My notifications",
      element: <NotificationPreferences />,
      layout: "portal",
      breadcrumb: [{ label: "Account" }, { label: "My notifications" }],
    },
    {
      path: "/authentication/roles",
      name: "Roles",
      permissions: ["see_roles"],
      element: <Roles />,
      layout: "portal",
      breadcrumb: [{ label: "Authentication", to: "/authentication" }, { label: "Roles" }],
      children: [
        {
          path: "/authentication/roles/add",
          name: "Add Role",
          permissions: ["add_role"],
          element: <RoleAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Roles" }, { label: "Add / Edit Role" }],
        },
        {
          path: "/authentication/roles/edit/:id",
          name: "Edit Role",
          permissions: ["update_role"],
          element: <RoleAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Roles" }, { label: "Add / Edit Role" }],
        },
      ],
    },
    {
      path: "/admin/vendor-payment-methods",
      name: "Vendor Payment Methods",
      permissions: ["admin_see_payment_methods"],
      element: <VendorPaymentMethods />,
      layout: "portal",
      breadcrumb: [{ label: "Vendor Payment Methods" }],
    },
    {
      path: "/admin/integrations",
      name: "Integrations",
      permissions: ["manage_supplier_integration"],
      element: <IntegrationRoot />,
      children: [
        {
          path: "/admin/integrations/staging",
          name: "Staging Queue",
          permissions: ["manage_supplier_integration"],
          element: <StagingQueue />,
          layout: "portal",
          breadcrumb: [{ label: "Catalog Stagings" }, { label: "Detail" }],
        },
        {
          path: "/admin/integrations/staging/:id",
          name: "Staging Detail",
          permissions: ["manage_supplier_integration"],
          element: <StagingDetail />,
        },
      ],
    },
    {
      path: "/authentication/permissions",
      name: "Permissions",
      permissions: ["see_permissions"],
      element: <Permissions />,
      layout: "portal",
      breadcrumb: [{ label: "Authentication", to: "/authentication" }, { label: "Permissions" }],
      children: [
        {
          path: "/authentication/permissions/add",
          name: "Add Permission",
          permissions: ["add_permission"],
          element: <PermissionAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Permissions" }, { label: "Add / Edit Permission" }],
        },
        {
          path: "/authentication/permissions/edit/:id",
          name: "Edit Permission",
          permissions: ["update_permission"],
          element: <PermissionAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Permissions" }, { label: "Add / Edit Permission" }],
        },
      ],
    },
    {
      path: "/authentication/users",
      name: "Users",
      permissions: ["see_users"],
      element: <Users />,
      layout: "portal",
      breadcrumb: [{ label: "Authentication", to: "/authentication" }, { label: "Users" }],
      children: [
        {
          path: "/authentication/users/add",
          name: "Add User",
          permissions: ["add_user"],
          element: <UserAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Users" }, { label: "Add / Edit User" }],
        },
        {
          path: "/authentication/users/edit/:id",
          name: "Edit User",
          permissions: ["update_user"],
          element: <UserAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Users" }, { label: "Add / Edit User" }],
        },
        {
          path: "/authentication/users/logs/:user_id",
          name: "User Logs",
          permissions: ["see_user_logs"],
          element: <UserLogs />,
          layout: "portal",
          breadcrumb: [{ label: "Authentication", to: "/authentication" }, { label: "User Logs" }],
        },
        {
          path: "/authentication/users/detail/:user_id",
          name: "User Detail",
          permissions: ["see_users"],
          element: <UserDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Users", to: "/authentication/users" }, { label: "Detail" }],
        },
      ],
    },
    {
      path: "/authentication/users-types",
      name: "Users",
      permissions: ["see_user_types"],
      element: <UserTypes />,
      layout: "portal",
      breadcrumb: [{ label: "Authentication", to: "/authentication" }, { label: "User Types" }],
      children: [],
    },
  ],
};

const customerRoutes: RoutesProps = {
  path: "/customers",
  name: "Customers",
  icon: "airplay",
  element: <Navigate to="/customers/list" />,
  children: [
    {
      path: "/customers/list",
      name: "Customers",
      permissions: ["see_customers"],
      element: <Customers />,
      // ── Theme migration (Phase 3): new PortalLayout shell ──
      layout: "portal",
      breadcrumb: [{ label: "Customers" }],
    },
    {
      path: "/customers/exclusive-referrals",
      name: "Exclusive referrals",
      permissions: ["admin_see_exclusive_referrals"],
      element: <ExclusiveReferrals />,
      layout: "portal",
      breadcrumb: [{ label: "Customers", to: "/customers/list" }, { label: "Exclusive referrals" }],
    },
  ],
};

const agentRoutes: RoutesProps = {
  path: "/agents",
  name: "Agent Groups",
  icon: "airplay",
  element: <Navigate to="/agents/list" />,
  children: [
    {
      path: "/agents/list",
      name: "Agents",
      permissions: ["admin_view_agent_groups"],
      element: <Agents />,
      // ── Theme migration (Phase 3): new PortalLayout shell ──
      layout: "portal",
      breadcrumb: [{ label: "Agents" }],
    },
    {
      // Agent 360 — read-only dossier. Gated on admin_get_agent (same slug the
      // list/group View buttons check), so a low-permission admin never lands
      // on a page they can't load.
      path: "/agents/detail/:id",
      name: "Agent Detail",
      permissions: ["admin_get_agent"],
      element: <AgentDetail />,
      layout: "portal",
      breadcrumb: [{ label: "Agents", to: "/agents/list" }, { label: "Agent 360" }],
    },
    {
      path: "/agents/agent-groups",
      name: "Agent Groups",
      permissions: ["admin_view_agent_groups"],
      element: <AgentGroups />,
      // ── Theme migration (Phase 3): new PortalLayout shell ──
      layout: "portal",
      breadcrumb: [
        { label: "Agents", to: "/agents/list" },
        { label: "Agent Groups" },
      ],
    },
    {
      path: "/agents/agent-groups/add",
      name: "Add Agent Groups",
      permissions: ["admin_add_agent_groups"],
      element: <AgentGroupsAddEdit />,
      layout: "portal",
      breadcrumb: [{ label: "Agent Groups" }, { label: "Add / Edit Agent Group" }],
    },
    {
      path: "/agents/agent-groups/edit/:id",
      name: "Edit Agent Groups",
      permissions: ["admin_edit_agent_groups"],
      element: <AgentGroupsAddEdit />,
      layout: "portal",
      breadcrumb: [{ label: "Agent Groups" }, { label: "Add / Edit Agent Group" }],
    },
    {
      path: "/agents/agent-groups/:id/detail",
      name: "Detail Agent Groups",
      permissions: ["admin_detail_agent_groups"],
      element: <AgentGroupsDetail />,
      layout: "portal",
      breadcrumb: [{ label: "Agent Groups", to: "/agents/agent-groups" }, { label: "<group name> || Detail" }],
    },
    {
      path: "/agents/agent-groups/:id/detail/add",
      name: "Add Agent Group",
      permissions: ["admin_detail_agent_groups"],
      element: <AgentGroupsDetailAddEdit />,
    },
    {
      path: "/agents/agent-groups/:id/detail/:tab",
      name: "Detail Agent Groups",
      permissions: ["admin_detail_agent_groups"],
      element: <AgentGroupsDetail />,
      layout: "portal",
      breadcrumb: [{ label: "Agent Groups", to: "/agents/agent-groups" }, { label: "<group name> || Detail" }],
    },
  ],
};

const parameterRoutes: RoutesProps = {
  path: "/parameters",
  name: "Parameters",
  icon: "lock",
  element: <Navigate to="/parameters/places/countries" />,
  children: [
    {
      path: "/parameters/cars/brands",
      name: "Car Brands",
      permissions: ["see_car_brands"],
      element: <CarBrands />,
      layout: "portal",
      breadcrumb: [{ label: "Parameters", to: "/parameters" }, { label: "Car Brands" }],
      children: [
        {
          path: "/parameters/cars/brands/add",
          name: "Add Car Brand",
          permissions: ["add_new_car_brands"],
          element: <CarBrandsAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Car Brands" }, { label: "Add / Edit Car Brand" }],
        },
        {
          path: "/parameters/cars/brands/edit/:id",
          name: "Edit Car Brand",
          permissions: ["update_car_brands"],
          element: <CarBrandsAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Car Brands" }, { label: "Add / Edit Car Brand" }],
        },
      ],
    },
    {
      path: "/parameters/cars/models",
      name: "Car Models",
      permissions: ["see_car_models"],
      element: <CarModels />,
      layout: "portal",
      breadcrumb: [{ label: "Cars", to: "/parameters/cars/brands" }, { label: "Car Models" }],
      children: [
        {
          path: "/parameters/cars/models/add",
          name: "Add Car Model",
          permissions: ["add_car_models"],
          element: <CarModelsAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Car Models" }, { label: "Add / Edit Car Model" }],
        },
        {
          path: "/parameters/cars/models/edit/:id",
          name: "Edit Car Model",
          permissions: ["update_car_models"],
          element: <CarModelsAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Car Models" }, { label: "Add / Edit Car Model" }],
        },
      ],
    },
    {
      path: "/parameters/infos/info-groups",
      name: "Info Groups",
      permissions: ["see_info_groups"],
      element: <InfoGroups />,
      layout: "portal",
      breadcrumb: [{ label: "Info Groups" }],
      children: [
        {
          path: "/parameters/infos/info-groups/add",
          name: "Add Info Group",
          permissions: ["add_info_groups"],
          element: <InfoGroupAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Info Groups" }, { label: "Add / Edit Info Group" }],
        },
        {
          path: "/parameters/infos/info-groups/edit/:id",
          name: "Edit Info Group",
          permissions: ["update_info_groups"],
          element: <InfoGroupAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Info Groups" }, { label: "Add / Edit Info Group" }],
        },
      ],
    },
    {
      path: "/parameters/allergens",
      name: "Allergens",
      permissions: ["see_allergens"],
      element: <Allergens />,
      layout: "portal",
      breadcrumb: [{ label: "Parameters", to: "/parameters" }, { label: "Allergens" }],
      children: [
        {
          path: "/parameters/allergens/add",
          name: "Add Allergen",
          permissions: ["add_allergens"],
          element: <AllergensAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Allergens" }, { label: "Add / Edit Allergen" }],
        },
        {
          path: "/parameters/allergens/edit/:id",
          name: "Edit Allergen",
          permissions: ["update_allergens"],
          element: <AllergensAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Allergens" }, { label: "Add / Edit Allergen" }],
        },
      ],
    },
    {
      path: "/parameters/currencies",
      name: "Currencies",
      permissions: ["see_currencies"],
      element: <Currencies />,
      layout: "portal",
      breadcrumb: [{ label: "Parameters", to: "/parameters" }, { label: "Currencies" }],
      children: [
        {
          path: "/parameters/currencies/add",
          name: "Add Currency",
          permissions: ["add_currencies"],
          element: <CurrenciesAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Currencys" }, { label: "Add / Edit Currency" }],
        },
        {
          path: "/parameters/currencies/edit/:id",
          name: "Edit Currency",
          permissions: ["update_currency"],
          element: <CurrenciesAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Currencys" }, { label: "Add / Edit Currency" }],
        },
      ],
    },
    {
      path: "/parameters/services",
      name: "Services",
      permissions: ["see_services"],
      element: <ServicesDefaults />,
      layout: "portal",
      breadcrumb: [{ label: "Parameters", to: "/parameters" }, { label: "Services" }],
    },
    {
      path: "/parameters/compliance-requirements",
      name: "Compliance Requirements",
      permissions: ["admin_manage_service_availability"],
      element: <ComplianceRequirements />,
      layout: "portal",
      breadcrumb: [{ label: "Parameters", to: "/parameters" }, { label: "Compliance Requirements" }],
    },
    {
      path: "/suppliers/activation-requests",
      name: "Service Activation Requests",
      permissions: ["admin_manage_service_availability"],
      element: <ServiceActivationRequests />,
      layout: "portal",
      breadcrumb: [{ label: "KYB & Compliance" }, { label: "Activation Requests" }],
    },
    {
      path: "/parameters/services/:id",
      name: "Service Detail",
      permissions: ["see_services"],
      element: <ServiceDetail />,
      layout: "portal",
      breadcrumb: [{ label: "Services", to: "/parameters/services" }, { label: "Detail" }],
    },
    {
      path: "/parameters/places/cities",
      name: "City",
      permissions: ["see_places_cities"],
      element: <Cities />,
      layout: "portal",
      breadcrumb: [{ label: "Places", to: "/parameters/places/countries" }, { label: "Cities" }],
      children: [
        {
          path: "/parameters/places/cities/add",
          name: "Add City",
          permissions: ["add_city"],
          element: <CityAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Citys" }, { label: "Add / Edit City" }],
        },
        {
          path: "/parameters/places/cities/edit/:id",
          name: "Edit City",
          permissions: ["update_cities"],
          element: <CityAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Citys" }, { label: "Add / Edit City" }],
        },
      ],
    },
    {
      path: "/parameters/likes/categories",
      name: "Like Category",
      permissions: ["see_like_categories"],
      element: <LikeCategories />,
      layout: "portal",
      breadcrumb: [{ label: "Parameters", to: "/parameters" }, { label: "Like Categories" }],
      children: [
        {
          path: "/parameters/likes/categories/add",
          name: "Add Like Category",
          permissions: ["add_like_category"],
          element: <LikeCategoryAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Like Categorys" }, { label: "Add / Edit Like Category" }],
        },
        {
          path: "/parameters/likes/categories/edit/:id",
          name: "Edit Like Category",
          permissions: ["update_like_category"],
          element: <LikeCategoryAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Like Categorys" }, { label: "Add / Edit Like Category" }],
        },
      ],
    },
    {
      path: "/parameters/infos/info-type-options",
      name: "Info Type Option",
      permissions: ["see_info_type_options"],
      element: <InfoTypeOptions />,
      layout: "portal",
      breadcrumb: [{ label: "Infos", to: "/parameters/infos/info-groups" }, { label: "Info Type Options" }],
      children: [
        {
          path: "/parameters/infos/info-type-options/add",
          name: "Add Info Type Option",
          permissions: ["add_info_type_options"],
          element: <InfoTypeOptionAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Info Type Options" }, { label: "Add / Edit Info Type Option" }],
        },
        {
          path: "/parameters/infos/info-type-options/edit/:id",
          name: "Edit Info Type Option",
          permissions: ["update_info_type_options"],
          element: <InfoTypeOptionAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Info Type Options" }, { label: "Add / Edit Info Type Option" }],
        },
      ],
    },
    {
      path: "/parameters/transportation-types",
      name: "Transportation Type",
      permissions: ["see_transportation_types"],
      element: <TransportationTypes />,
      layout: "portal",
      breadcrumb: [{ label: "Parameters", to: "/parameters" }, { label: "Transportation Types" }],
      children: [
        {
          path: "/parameters/transportation-types/add",
          name: "Add Transportation Type",
          permissions: ["add_transportation_types"],
          element: <TransportationTypeAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Transportation Types" }, { label: "Add / Edit Transportation Type" }],
        },
        {
          path: "/parameters/transportation-types/edit/:id",
          name: "Edit Transportation Type",
          permissions: ["update_transportation_types"],
          element: <TransportationTypeAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Transportation Types" }, { label: "Add / Edit Transportation Type" }],
        },
      ],
    },
    {
      path: "/parameters/languages",
      name: "Languages",
      permissions: ["see_languages"],
      element: <Languages />,
      layout: "portal",
      breadcrumb: [{ label: "Parameters", to: "/parameters" }, { label: "Languages" }],
      children: [
        {
          path: "/parameters/languages/add",
          name: "Add Languages",
          permissions: ["add_language"],
          element: <LanguagesAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Languages" }, { label: "Add / Edit Language" }],
        },
        {
          path: "/parameters/languages/edit/:id",
          name: "Edit Languages",
          permissions: ["update_language"],
          element: <LanguagesAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Languages" }, { label: "Add / Edit Language" }],
        },
      ],
    },
    {
      path: "/parameters/payment-methods",
      name: "Payment Methods",
      permissions: ["see_payment_methods"],
      element: <PaymentMethods />,
      layout: "portal",
      breadcrumb: [{ label: "Parameters", to: "/parameters" }, { label: "Payment Methods" }],
      children: [
        {
          path: "/parameters/payment-methods/add",
          name: "Payment Methods",
          permissions: ["add_payment_methods"],
          element: <PaymentMethodsAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Payment Methods" }, { label: "Add / Edit Payment Method" }],
        },
        {
          path: "/parameters/payment-methods/edit/:id",
          name: "Payment Methods",
          permissions: ["update_payment_methods"],
          element: <PaymentMethodsAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Payment Methods" }, { label: "Add / Edit Payment Method" }],
        },
      ],
    },
    {
      path: "/events/event-categories",
      name: "Event Category",
      permissions: ["see_event_categories"],
      element: <EventCategories />,
      layout: "portal",
      breadcrumb: [{ label: "Events", to: "/events/event-categories" }, { label: "Event Categories" }],
      children: [
        {
          path: "/events/event-categories/add",
          name: "Add Event Category",
          permissions: ["add_new_event_categories"],
          element: <EventCategoryAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Event Categorys" }, { label: "Add / Edit Event Category" }],
        },
        {
          path: "/events/event-categories/edit/:id",
          name: "Edit Event Category",
          permissions: ["update_event_categories"],
          element: <EventCategoryAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Event Categorys" }, { label: "Add / Edit Event Category" }],
        },
      ],
    },
    {
      path: "/events/list",
      name: "Events",
      permissions: ["admin_list_supplier_events"],
      element: <Events />,
      layout: "portal",
      breadcrumb: [{ label: "Events" }],
      children: [
        {
          path: "/events/add",
          name: "Add Event",
          permissions: ["admin_add_supplier_events"],
          element: <EventAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Events" }, { label: "Add / Edit Event" }],
        },
        {
          path: "/events/view/:id",
          name: "Event",
          permissions: ["admin_list_supplier_events"],
          element: <EventView />,
          layout: "portal",
          breadcrumb: [{ label: "Events", to: "/events/list" }, { label: "<event name>" }],
        },
        {
          path: "/events/edit/:id",
          name: "Edit Event",
          permissions: ["admin_edit_supplier_events"],
          element: <EventAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Events" }, { label: "Add / Edit Event" }],
        },
        {
          path: "/event/:id/attendees",
          name: "Event Attendees",
          permissions: ["admin_edit_supplier_events"],
          element: <EventAttendees />,
          layout: "portal",
          breadcrumb: [{ label: "Events", to: "/events/list" }, { label: "#<id>" }, { label: "Event Attendees" }],
        },
      ],
    },
    {
      path: "/tickets/list",
      name: "Tickets",
      permissions: ["agent_ticket_support"],
      element: <Tickets />,
      layout: "portal",
      breadcrumb: [{ label: "Tickets" }],
      children: [
        {
          path: "/tickets/edit/:id",
          name: "Edit Ticket",
          permissions: ["agent_ticket_support"],
          element: <TicketAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Tickets" }, { label: "Add / Edit Ticket" }],
        },
      ],
    },
    {
      path: "/tickets/categories",
      name: "Ticket Categories",
      permissions: ["admin_manage_ticket_categories"],
      element: <TicketCategories />,
      layout: "portal",
      breadcrumb: [{ label: "Tickets", to: "/tickets/list" }, { label: "Ticket Categories" }],
      children: [
        {
          path: "/tickets/categories/add",
          name: "Add Ticket Category",
          permissions: ["admin_manage_ticket_categories"],
          element: <TicketCategoryAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Ticket Categories", to: "/tickets/categories" }, { label: "Add / Edit Ticket Category" }],
        },
        {
          path: "/tickets/categories/edit/:id",
          name: "Edit Ticket Category",
          permissions: ["admin_manage_ticket_categories"],
          element: <TicketCategoryAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Ticket Categories", to: "/tickets/categories" }, { label: "Add / Edit Ticket Category" }],
        },
      ],
    },
    {
      path: "/parameters/places/states",
      name: "State",
      permissions: ["see_places_states"],
      element: <States />,
      layout: "portal",
      breadcrumb: [{ label: "Places", to: "/parameters/places/countries" }, { label: "States" }],
      children: [
        {
          path: "/parameters/places/states/add",
          name: "Add State",
          permissions: ["add_state"],
          element: <StateAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "States" }, { label: "Add / Edit State" }],
        },
        {
          path: "/parameters/places/states/edit/:id",
          name: "Edit State",
          permissions: ["update_state"],
          element: <StateAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "States" }, { label: "Add / Edit State" }],
        },
      ],
    },
    {
      path: "/parameters/likes",
      name: "Like",
      permissions: ["see_likes"],
      element: <Likes />,
      layout: "portal",
      breadcrumb: [{ label: "Parameters", to: "/parameters" }, { label: "Likes" }],
      children: [
        {
          path: "/parameters/likes/add",
          name: "Add Like",
          permissions: ["add_likes"],
          element: <LikesAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Likes" }, { label: "Add / Edit Like" }],
        },
        {
          path: "/parameters/likes/edit/:id",
          name: "Edit Like",
          permissions: ["update_likes"],
          element: <LikesAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Likes" }, { label: "Add / Edit Like" }],
        },
      ],
    },
    {
      path: "/parameters/infos/info-types",
      name: "Info Type",
      permissions: ["see_info_types"],
      element: <InfoTypes />,
      layout: "portal",
      breadcrumb: [{ label: "Infos", to: "/parameters/infos/info-groups" }, { label: "Info Types" }],
      children: [
        {
          path: "/parameters/infos/info-types/add",
          name: "Add Info Type",
          permissions: ["add_new_info_type"],
          element: <InfoTypeAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Info Types" }, { label: "Add / Edit Info Type" }],
        },
        {
          path: "/parameters/infos/info-types/edit/:id",
          name: "Edit Info Type",
          permissions: ["update_info_type"],
          element: <InfoTypeAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Info Types" }, { label: "Add / Edit Info Type" }],
        },
      ],
    },
    {
      path: "/parameters/places/countries",
      name: "Countries",
      permissions: ["see_places_countries"],
      element: <Countries />,
      layout: "portal",
      breadcrumb: [{ label: "Parameters", to: "/parameters" }, { label: "Countries" }],
      children: [
        {
          path: "/parameters/places/countries/add",
          name: "Add Countries",
          permissions: ["add_countries"],
          element: <CountryAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Countrys" }, { label: "Add / Edit Country" }],
        },
        {
          path: "/parameters/places/countries/edit/:id",
          name: "Update Countries",
          permissions: ["update_countries"],
          element: <CountryAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Countrys" }, { label: "Add / Edit Country" }],
        },
      ],
    },
    {
      path: "/parameters/infos/info-types",
      name: "Info Type",
      permissions: ["see_info_types"],
      element: <InfoTypes />,
      layout: "portal",
      breadcrumb: [{ label: "Infos", to: "/parameters/infos/info-groups" }, { label: "Info Types" }],
      children: [
        {
          path: "/parameters/infos/info-types/add",
          name: "Add Info Type",
          permissions: ["add_new_info_type"],
          element: <InfoTypeAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Info Types" }, { label: "Add / Edit Info Type" }],
        },
        {
          path: "/parameters/infos/info-types/edit/:id",
          name: "Edit Info Type",
          permissions: ["update_info_type"],
          element: <InfoTypeAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Info Types" }, { label: "Add / Edit Info Type" }],
        },
      ],
    },
    {
      path: "/parameters/infos/infos",
      name: "Info",
      permissions: ["see_infos"],
      element: <Infos />,
      layout: "portal",
      breadcrumb: [{ label: "Parameters", to: "/parameters" }, { label: "Infos" }],
      children: [
        {
          path: "/parameters/infos/infos/add",
          name: "Add Info",
          permissions: ["add_info"],
          element: <InfoAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Infos" }, { label: "Add / Edit Info" }],
        },
        {
          path: "/parameters/infos/infos/edit/:id",
          name: "Edit Info",
          permissions: ["update_info"],
          element: <InfoAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Infos" }, { label: "Add / Edit Info" }],
        },
      ],
    },
    {
      path: "/vendors/list",
      name: "Vendors",
      permissions: ["see_vendors"],
      element: <Vendors />,
      layout: "portal",
      breadcrumb: [{ label: "Integrations", to: "/vendors/list" }, { label: "Vendors" }],
      children: [
        {
          path: "/vendors/add",
          name: "Add Vendor",
          permissions: ["add_vendor"],
          element: <VendorAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Vendors" }, { label: "Add / Edit Vendor" }],
        },
        {
          path: "/vendors/edit/:id",
          name: "Edit Vendor",
          permissions: ["update_vendor"],
          element: <VendorAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Vendors" }, { label: "Add / Edit Vendor" }],
        },
        {
          path: "/vendors/detail/:id",
          name: "Vendor Detail",
          permissions: ["see_vendors"],
          element: <VendorDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Vendors" }, { label: "Detail" }],
        },
        {
          path: "/vendors/detail/:vendorId/segments/:segmentId",
          name: "Vendor Segment Detail",
          permissions: ["see_vendor_segment"],
          element: <VendorSegmentDetail />,
        },
      ],
    },
    {
      path: "/vendor/invoices",
      name: "Vendor Invoices",
      permissions: ["see_vendor_invoices"],
      element: <VendorInvoices />,
      layout: "portal",
      breadcrumb: [{ label: "Vendors", to: "/vendors/list" }, { label: "Vendor Invoices" }],
      children: [
        {
          path: "/vendor/invoices/add",
          name: "Add Invoice",
          permissions: ["admin_create_invoice"],
          element: <VendorInvoicesAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Vendor Invoices" }, { label: "Add / Edit Vendor Invoice" }],
        },
        {
          path: "/vendor/invoices/edit/:id",
          name: "Edit Invoice",
          permissions: ["admin_edit_invoice"],
          element: <VendorInvoicesAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Vendor Invoices" }, { label: "Add / Edit Vendor Invoice" }],
        },
        {
          path: "/vendor/invoices/:id",
          name: "Invoice Detail",
          permissions: ["admin_edit_invoice"],
          element: <VendorInvoicesDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Vendor Invoices", to: "/vendor/invoices" }, { label: "Detail" }],
        },
      ],
    },
    {
      path: "/vendor/vendor-apis/:vendor_id",
      name: "Vendor Apis",
      permissions: ["see_vendor_apis"],
      element: <VendorApis />,
      layout: "portal",
      breadcrumb: [{ label: "Vendors", to: "/vendors/list" }, { label: "Vendor APIs" }],
      children: [
        {
          path: "/vendor/vendor-apis/add/:vendor_id",
          name: "Add Vendor Api",
          permissions: ["add_new_vendor_api_key"],
          element: <VendorApiAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Vendor APIs" }, { label: "Add / Edit Vendor API" }],
        },
        {
          path: "/vendor/vendor-apis/edit/:vendor_id/:id",
          name: "Edit Vendor Api",
          permissions: ["update_vendor_api_keys"],
          element: <VendorApiAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Vendor APIs" }, { label: "Add / Edit Vendor API" }],
        },
        {
          path: "/vendor/vendor-apis/detail/:vendor_id/:id",
          name: "See Vendor Api Key",
          permissions: ["see_vendor_api_keys"],
          element: <VendorApiDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Vendor APIs" }, { label: "Detail" }],
        },
      ],
    },
    {
      path: "/suppliers/list",
      name: "Suppliers",
      permissions: ["see_suppliers"],
      element: <Suppliers />,
      // ── Theme migration (Phase 3): new PortalLayout shell ──
      layout: "portal",
      breadcrumb: [{ label: "Suppliers" }],
      children: [
        {
          path: "/suppliers/add",
          name: "Add Supplier",
          permissions: ["add_supplier"],
          element: <SupplierAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Suppliers", to: "/suppliers" }, { label: "Add Supplier" }],
        },
        {
          path: "/suppliers/:id/edit",
          name: "Edit Supplier",
          permissions: ["update_supplier"],
          element: <SupplierAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Suppliers", to: "/suppliers" }, { label: "Edit Supplier" }],
        },
        {
          path: "/suppliers/:id",
          name: "See Supplier Detail",
          permissions: ["see_supplier_detail"],
          element: <SupplierDetail />,
          layout: "portal", // Theme migration (Phase 3): supplier-detail shell
          children: [
            // Integration is a tab like the others: /suppliers/:id/integration resolves
            // through the generic :name route below, so it renders inside the supplier
            // shell (header + sub-menu) instead of on a bare portal page.
            {
              path: "/suppliers/:id/:name",
              name: "See Supplier Detail",
              permissions: ["see_supplier_detail"],
              element: <SupplierDetail />,
              layout: "portal", // Theme migration (Phase 3): supplier-detail shell
            },
            {
              path: "/suppliers/:id/:name/:itemId",
              name: "See Extra Detail",
              permissions: ["see_supplier_detail"],
              element: <SupplierDetail />,
              layout: "portal", // Theme migration (Phase 3): supplier-detail shell
            },
            {
              path: "/suppliers/:id/:name/:itemId/:secondName",
              name: "See Child Page",
              permissions: ["see_supplier_detail"],
              element: <SupplierDetail />,
              layout: "portal", // Theme migration (Phase 3): supplier-detail shell
            },
            {
              path: "/suppliers/:id/:name/add",
              name: "See Supplier Add ",
              permissions: ["see_supplier_detail"],
              element: <SupplierDetail />,
              layout: "portal", // Theme migration (Phase 3): supplier-detail shell
            },
            {
              path: "/suppliers/:id/:name/:itemId/edit",
              name: "See Supplier Edit",
              permissions: ["see_supplier_detail"],
              element: <SupplierDetail />,
              layout: "portal", // Theme migration (Phase 3): supplier-detail shell
            },
          ],
        },
      ],
    },
    {
      path: "/suppliers/waiting-approvals",
      name: "Waiting Approvals",
      permissions: ["see_suppliers"],
      element: <WaitingApprovals/>,
      layout: "portal",
      breadcrumb: [{ label: "KYB & Compliance" }, { label: "Supplier Applications" }],
    },
    {
      path: "/compliance/documents",
      name: "Document Approvals",
      permissions: ["admin_review_compliance_document"],
      element: <ComplianceDocuments />,
      layout: "portal",
      breadcrumb: [{ label: "KYB & Compliance" }, { label: "Document Approvals" }],
    },
    {
      path: "/compliance/contracts",
      name: "Signed Contracts",
      permissions: ["admin_see_contracts"],
      element: <ComplianceContracts />,
      layout: "portal",
      breadcrumb: [{ label: "KYB & Compliance" }, { label: "Signed Contracts" }],
    },
    {
      path: "/parameters/mobile-pages",
      name: "Mobile Pages",
      permissions: ["see_mobile_pages"],
      element: <MobilePages />,
      layout: "portal",
      breadcrumb: [{ label: "Parameters", to: "/parameters" }, { label: "Mobile Pages" }],
      children: [
        {
          path: "/parameters/mobile-pages/add",
          name: "Add Mobile Page",
          permissions: ["add_mobile_page"],
          element: <MobilePageAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Mobile Pages" }, { label: "Add / Edit Mobile Page" }],
        },
        {
          path: "/parameters/mobile-pages/edit/:id",
          name: "Edit Mobile Page",
          permissions: ["update_mobile_page"],
          element: <MobilePageAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Mobile Pages" }, { label: "Add / Edit Mobile Page" }],
        },
        {
          path: "/parameters/mobile-pages/detail/:id",
          name: "Mobile Page Detail",
          permissions: ["see_mobile_page"],
          element: <MobilePageDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Mobile Pages" }, { label: "Detail" }],
        },
      ],
    },
    {
      path: "/storage-health",
      name: "Storage Health",
      // Same permission the endpoint enforces (admin_list_users). Matching it
      // matters: a route gated more loosely than its API just renders an
      // error, and more tightly hides a page the user is allowed to see.
      permissions: ["admin_list_users"],
      element: <StorageHealth />,
      layout: "portal",
      breadcrumb: [{ label: "Storage Health" }],
    },
    {
      path: "/system-features",
      name: "System Features",
      permissions: ["see_system_features"],
      element: <SystemFeatures />,
      layout: "portal",
      breadcrumb: [{ label: "System Features" }],
      children: [
        {
          path: "/system-features/add",
          name: "Add System Feature",
          permissions: ["add_system_feature"],
          element: <SystemFeatureAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "System Features" }, { label: "Add / Edit System Feature" }],
        },
        {
          path: "/system-features/edit/:id",
          name: "Edit System Feature",
          permissions: ["update_system_feature"],
          element: <SystemFeatureAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "System Features" }, { label: "Add / Edit System Feature" }],
        },
        {
          path: "/system-features/detail/:id",
          name: "System Feature Detail",
          permissions: ["see_system_features"],
          element: <SystemFeatureDetail />,
          layout: "portal",
          breadcrumb: [{ label: "System Features", to: "/system-features" }, { label: "Detail" }],
        },
      ],
    },
    {
      path: "/badges",
      name: "Badges",
      permissions: ["see_badges"],
      element: <Badges />,
      // ── Theme migration (Phase 3): new PortalLayout shell ──
      layout: "portal",
      breadcrumb: [
        { label: "Recommended Content", to: "/recommended-content" },
        { label: "Badges" },
      ],
      children: [
        {
          path: "/badges/:id",
          name: "Badge Detail",
          permissions: ["see_badge"],
          element: <BadgeDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Recommended Content", to: "/recommended-content" }, { label: "Badges", to: "/badges" }, { label: "<displayName>" }],
        },
      ],
    },
    {
      path: "/tags",
      name: "Tags",
      permissions: ["see_tags"],
      element: <Tags />,
      // ── Theme migration pilot (Phase 1): renders in the new PortalLayout shell ──
      layout: "portal",
      breadcrumb: [
        { label: "Recommended Content", to: "/recommended-content" },
        { label: "Tags" },
      ],
      children: [
        {
          path: "/tags/:id",
          name: "Tag Detail",
          permissions: ["see_tag"],
          element: <TagDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Tags", to: "/tags" }, { label: "<tag.displayName> || Detail" }],
        },
      ],
    },
    {
      path: "/recommended-content/categories",
      name: "Recommended Content Categories",
      permissions: ["see_recommended_content_categories"],
      element: <RecommendedContentCategories />,
      // ── Theme migration (Phase 3): new PortalLayout shell ──
      layout: "portal",
      breadcrumb: [
        { label: "Recommended Content", to: "/recommended-content" },
        { label: "Categories" },
      ],
      children: [
        {
          path: "/recommended-content/categories/:id",
          name: "Category Detail",
          permissions: ["see_recommended_content_category"],
          element: <RecommendedContentCategoryDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Recommended Content", to: "/recommended-content" }, { label: "Categories", to: "/recommended-content/categories" }, { label: "<displayName>" }],
        },
      ],
    },
    {
      path: "/recommended-content",
      name: "Recommended Content",
      permissions: ["see_recommended_content"],
      element: <RecommendedContent />,
      layout: "portal",
      breadcrumb: [{ label: "Recommended Content" }],
      children: [
        {
          path: "/recommended-content/add",
          name: "Add Content",
          permissions: ["add_recommended_content"],
          element: <RecommendedContentAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Recommended Content", to: "/recommended-content" }, { label: "Add/Edit Content" }],
        },
        {
          path: "/recommended-content/edit/:id",
          name: "Edit Content",
          permissions: ["update_recommended_content"],
          element: <RecommendedContentAddEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Recommended Content", to: "/recommended-content" }, { label: "Add/Edit Content" }],
        },
        {
          path: "/recommended-content/:id",
          name: "Content Detail",
          permissions: ["see_recommended_content"],
          element: <RecommendedContentDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Recommended Content", to: "/recommended-content" }, { label: "Detail" }],
        },
      ],
    },
    {
      path: "/ai/policies",
      name: "AI policies",
      permissions: ["admin_manage_ai_policies"],
      element: <AiPoliciesPage />,
      layout: "portal",
      breadcrumb: [{ label: "Customer Support" }, { label: "AI policies" }],
    },
    {
      path: "/ai/system-prompts",
      name: "AI system prompts",
      permissions: ["super_admin_manage_ai_prompts"],
      element: <AiPromptsPage />,
      layout: "portal",
      breadcrumb: [{ label: "Customer Support" }, { label: "AI system prompts" }],
    },
    {
      path: "/ai-agent",
      name: "AI Agent",
      permissions: ["see_ai_agent_config"],
      element: <AIAgentConfigPage />,
      layout: "portal",
      breadcrumb: [{ label: "AI Agent" }],
    },
    {
      path: "/parameters/support-auto-reply",
      name: "Support Auto-Reply",
      permissions: ["see_ai_agent_config"],
      element: <SupportAutoReplyPage />,
      layout: "portal",
      breadcrumb: [{ label: "Customer Support" }, { label: "Support Auto-Reply" }],
    },
    {
      path: "/parameters/dispatch",
      name: "Dispatch",
      permissions: ["see_dispatch_config"],
      element: <DispatchSettingsPage />,
      layout: "portal",
      breadcrumb: [{ label: "Dispatch" }],
    },
    {
      path: "/parameters/offer-approval",
      name: "Offer Approval",
      permissions: ["see_offer_approval_policy"],
      element: <OfferApprovalPage />,
      layout: "portal",
      breadcrumb: [{ label: "Offer Approval" }],
    },
    {
      path: "/parameters/supplier-request-pricing",
      name: "Supplier-made Requests",
      permissions: ["admin_see_supplier_request_pricing"],
      element: <SupplierRequestPricingPage />,
      layout: "portal",
      breadcrumb: [{ label: "Supplier-made Requests" }],
    },
    {
      path: "/admin/airports",
      name: "Airports",
      permissions: ["admin_see_airports"],
      element: <Airports />,
      layout: "portal",
      breadcrumb: [{ label: "Airports" }],
    },
    {
      path: "/admin/jet-classes",
      name: "Jet Classes",
      permissions: ["admin_see_jet_classes"],
      element: <JetClasses />,
      layout: "portal",
      breadcrumb: [{ label: "Jet Classes" }],
      children: [
        {
          path: "/admin/jet-classes/:code",
          name: "Jet Class Detail",
          permissions: ["admin_see_jet_classes"],
          element: <JetClassDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Jet Classes", to: "/aviation/jet-classes" }, { label: "<jet class name or code>" }],
        },
      ],
    },
    {
      path: "/admin/aircrafts",
      name: "Aircrafts",
      permissions: ["admin_see_aircrafts"],
      element: <Aircrafts />,
      layout: "portal",
      breadcrumb: [{ label: "Aircrafts" }],
      children: [
        {
          path: "/admin/aircrafts/:id",
          name: "Aircraft Detail",
          permissions: ["admin_see_aircrafts"],
          element: <AircraftDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Aircrafts", to: "/aviation/aircrafts" }, { label: "Detail" }],
        },
      ],
    },
    {
      path: "/admin/empty-legs",
      name: "Empty Legs",
      permissions: ["admin_see_empty_legs"],
      element: <EmptyLegs />,
      layout: "portal",
      breadcrumb: [{ label: "Empty Legs" }],
      children: [
        {
          path: "/admin/empty-legs/:id",
          name: "Empty Leg Detail",
          permissions: ["admin_see_empty_legs"],
          element: <EmptyLegDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Empty Legs", to: "/aviation/empty-legs" }, { label: "#<id>" }],
        },
      ],
    },
    {
      path: "/admin/hotels",
      name: "Hotels",
      permissions: ["see_suppliers"],
      element: <HotelsList />,
      layout: "portal",
      breadcrumb: [{ label: "Hotels" }],
      children: [
        {
          path: "/admin/hotels/:id",
          name: "Hotel",
          permissions: ["see_suppliers"],
          element: <HotelView />,
          layout: "portal",
          breadcrumb: [{ label: "Hotels", to: "/admin/hotels" }, { label: "<hotel name>" }],
        },
        {
          path: "/admin/hotels/:id/edit",
          name: "Edit Hotel",
          permissions: ["edit_page_hotel_to_supplier"],
          element: <HotelEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Hotels", to: "/admin/hotels" }, { label: "#<id>" }, { label: "Edit" }],
        },
      ],
    },
    {
      path: "/admin/inventory/submissions",
      name: "Submissions",
      permissions: ["admin_moderate_yachts", "admin_moderate_cars", "admin_approve_experiences", "admin_moderate_hotels", "admin_moderate_restaurants", "admin_moderate_events", "admin_manage_aircrafts", "publish_recommended_content"],
      element: <SubmissionsQueue />,
      layout: "portal",
      breadcrumb: [{ label: "Submissions" }],
    },
    {
      path: "/admin/inventory/pending-changes",
      name: "Pending Changes",
      permissions: ["admin_moderate_yachts", "admin_moderate_cars", "admin_approve_experiences", "admin_moderate_hotels", "admin_moderate_restaurants", "admin_moderate_events", "admin_manage_aircrafts", "publish_recommended_content"],
      element: <PendingChangesQueue />,
      layout: "portal",
      breadcrumb: [{ label: "Pending Changes" }],
    },
    {
      path: "/admin/inventory/moderation-policy",
      name: "Inventory Approval",
      permissions: ["admin_manage_moderation_policy"],
      element: <ModerationPolicyPage />,
      layout: "portal",
      breadcrumb: [{ label: "Inventory Approval" }],
    },
    {
      path: "/admin/promotions",
      name: "Promotions",
      permissions: ["admin_manage_promotions"],
      element: <PromotionsPage />,
      layout: "portal",
      breadcrumb: [{ label: "Content" }, { label: "Promotions" }],
    },
    {
      path: "/content/knowledge-base",
      name: "Knowledge base",
      permissions: ["admin_manage_knowledge_base"],
      element: <KnowledgeBasePage />,
      layout: "portal",
      breadcrumb: [{ label: "Customer Support" }, { label: "Knowledge base" }],
    },
    {
      path: "/admin/promotions/settings",
      name: "Promotions settings",
      permissions: ["admin_manage_promotions"],
      element: <PromotionSettingsPage />,
      layout: "portal",
      breadcrumb: [{ label: "Content" }, { label: "Promotions", to: "/admin/promotions" }, { label: "Settings" }],
    },
    {
      path: "/admin/cross-sell/settings",
      name: "Cross-sell settings",
      permissions: ["admin_manage_promotions"],
      element: <CrossSellSettingsPage />,
      layout: "portal",
      breadcrumb: [{ label: "Content" }, { label: "Cross-sell settings" }],
    },
    {
      path: "/admin/promotions/:id",
      name: "Promotion detail",
      permissions: ["admin_manage_promotions"],
      element: <PromotionDetailPage />,
      layout: "portal",
      breadcrumb: [{ label: "Content" }, { label: "Promotions", to: "/admin/promotions" }, { label: "Detail" }],
    },
    {
      path: "/admin/restaurants",
      name: "Restaurants",
      permissions: ["see_suppliers"],
      element: <RestaurantsList />,
      layout: "portal",
      breadcrumb: [{ label: "Restaurants" }],
      children: [
        {
          path: "/admin/restaurants/:id",
          name: "Restaurant",
          permissions: ["see_suppliers"],
          element: <RestaurantView />,
          layout: "portal",
          breadcrumb: [{ label: "Restaurants", to: "/admin/restaurants" }, { label: "<restaurant name>" }],
        },
        {
          path: "/admin/restaurants/:id/edit",
          name: "Edit Restaurant",
          permissions: ["admin_update_supplier_restaurant"],
          element: <RestaurantEdit />,
          layout: "portal",
          breadcrumb: [{ label: "Restaurants", to: "/admin/restaurants" }, { label: "#<id>" }, { label: "Edit" }],
        },
      ],
    },
    {
      path: "/admin/yachts",
      name: "Yachts",
      permissions: ["admin_see_yachts"],
      element: <Yachts />,
      layout: "portal",
      breadcrumb: [{ label: "Yachts" }],
      children: [
        {
          path: "/admin/yachts/:id",
          name: "Yacht Detail",
          permissions: ["admin_see_yachts"],
          element: <YachtDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Yachts", to: "/admin/yachts" }, { label: "<yacht name> || Detail" }],
        },
      ],
    },
    {
      path: "/admin/experiences",
      name: "Experiences",
      permissions: ["admin_see_experiences"],
      element: <Experiences />,
      layout: "portal",
      breadcrumb: [{ label: "Experiences" }],
      children: [
        {
          path: "/admin/experiences/:id",
          name: "Experience Detail",
          permissions: ["admin_see_experiences"],
          element: <ExperienceDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Experiences", to: "/admin/experiences" }, { label: "<experience name> || Detail" }],
        },
      ],
    },
    {
      path: "/admin/security",
      name: "Security",
      element: <Security />,
      layout: "portal",
      breadcrumb: [{ label: "Security" }],
    },
    {
      path: "/admin/mfa-management",
      name: "MFA Management",
      permissions: ["update_user"],
      element: <MfaManagement />,
      layout: "portal",
      breadcrumb: [{ label: "MFA Management" }],
    },
    {
      path: "/admin/yacht-types",
      name: "Yacht Types",
      permissions: ["admin_see_yacht_types"],
      element: <YachtTypes />,
      layout: "portal",
      breadcrumb: [{ label: "Yacht Types" }],
      children: [
        {
          path: "/admin/yacht-types/:id",
          name: "Yacht Type Detail",
          permissions: ["admin_see_yacht_types"],
          element: <YachtTypeDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Yacht Types", to: "/yacht-types" }, { label: "<entityName || Detail>" }],
        },
      ],
    },
    {
      path: "/admin/cars",
      name: "Cars",
      permissions: ["admin_see_cars"],
      element: <Cars />,
      layout: "portal",
      breadcrumb: [{ label: "Cars" }],
      children: [
        {
          path: "/admin/cars/:id",
          name: "Car Detail",
          permissions: ["admin_see_cars"],
          element: <CarDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Cars", to: "/admin/cars" }, { label: "Detail" }],
        },
      ],
    },
    {
      path: "/content-components",
      name: "Content Components",
      permissions: ["see_content_components"],
      element: <ContentComponents />,
      layout: "portal",
      breadcrumb: [{ label: "Content Components" }],
    },
    {
      path: "/content-component-templates",
      name: "Content Component Templates",
      permissions: ["see_component_templates"],
      element: <ContentComponentTemplates />,
      layout: "portal",
      breadcrumb: [{ label: "Management", to: "/management" }, { label: "Content Component Templates" }],
    },
    {
      path: "/parameters/fx-rates",
      name: "FX Rates",
      permissions: ["see_fx_rates"],
      element: <FxRates />,
      layout: "portal",
      breadcrumb: [{ label: "Parameters", to: "/parameters" }, { label: "FX Rates" }],
    },
    {
      path: "/admin/payment-intents",
      name: "Payment Intents",
      permissions: ["see_payment_intents"],
      element: <PaymentIntents />,
      layout: "portal",
      breadcrumb: [{ label: "Payment Intents" }],
    },
    // ─── Finance ──────────────────────────────────────────────────────────
    {
      path: "/admin/finance/overview",
      name: "Finance Overview",
      permissions: ["admin_see_finance_overview"],
      element: <FinanceOverview />,
      layout: "portal",
      breadcrumb: [{ label: "Finance" }, { label: "Overview" }],
    },
    {
      path: "/admin/finance/withdrawals",
      name: "Withdrawals",
      permissions: ["admin_process_withdrawal"],
      element: <Withdrawals />,
      layout: "portal",
      breadcrumb: [{ label: "Finance" }, { label: "Withdrawals" }],
    },
    {
      // Registered twice so the screen is reachable from the menu without a
      // supplier: with no id it asks for one, rather than 404ing.
      path: "/admin/finance/statements",
      name: "Supplier Statements",
      permissions: ["view_supplier_accounts"],
      element: <SupplierStatement />,
      layout: "portal",
      breadcrumb: [{ label: "Finance" }, { label: "Supplier Statements" }],
    },
    {
      path: "/admin/finance/statements/:supplierId",
      name: "Supplier Statement",
      permissions: ["view_supplier_accounts"],
      element: <SupplierStatement />,
      layout: "portal",
      breadcrumb: [
        { label: "Finance" },
        { label: "Supplier Statements", to: "/admin/finance/statements" },
        { label: "Statement" },
      ],
    },
    {
      path: "/admin/finance/commission",
      name: "Commission",
      permissions: ["admin_see_commission_report"],
      element: <Commission />,
      layout: "portal",
      breadcrumb: [{ label: "Finance" }, { label: "Commission" }],
    },
    {
      path: "/admin/finance/accounts",
      name: "Ledger Accounts",
      permissions: ["view_supplier_accounts"],
      element: <LedgerAccounts />,
      layout: "portal",
      breadcrumb: [{ label: "Finance" }, { label: "Ledger Accounts" }],
    },
    {
      path: "/admin/finance/ledger",
      name: "Ledger Transactions",
      permissions: ["view_supplier_accounts"],
      element: <LedgerTransactions />,
      layout: "portal",
      breadcrumb: [{ label: "Finance" }, { label: "Ledger Transactions" }],
    },
    {
      path: "/admin/finance/periods",
      name: "Finance Periods",
      permissions: ["admin_close_period"],
      element: <FinancePeriods />,
      layout: "portal",
      breadcrumb: [{ label: "Finance" }, { label: "Periods" }],
    },
    {
      path: "/admin/finance/referrals",
      name: "Referrals",
      permissions: ["admin_see_commission_report"],
      element: <FinanceReferrals />,
      layout: "portal",
      breadcrumb: [{ label: "Finance" }, { label: "Referrals" }],
    },
    {
      path: "/admin/finance/split",
      name: "Refund Split",
      permissions: ["admin_configure_refund_split"],
      element: <FinanceSplit />,
      layout: "portal",
      breadcrumb: [{ label: "Finance" }, { label: "Refund Split" }],
    },
    {
      path: "/admin/finance/reconciliation",
      name: "Reconciliation",
      permissions: ["admin_see_reconciliation"],
      element: <Reconciliation />,
      layout: "portal",
      breadcrumb: [{ label: "Finance" }, { label: "Reconciliation" }],
    },
    {
      path: "/admin/offer-transfers",
      name: "Offer Transfers",
      permissions: ["admin_manage_offers"],
      element: <OfferTransfers />,
      layout: "portal",
      breadcrumb: [{ label: "Offer Transfers" }],
    },
    {
      path: "/admin/notification-logs",
      name: "Notification Logs",
      permissions: ["admin_see_notification"],
      element: <NotificationLogs />,
      layout: "portal",
      breadcrumb: [{ label: "Notification Logs" }],
    },
    {
      path: "/admin/notification-settings",
      name: "Notification Settings",
      permissions: ["admin_see_notification"],
      element: <NotificationSettings />,
      layout: "portal",
      breadcrumb: [{ label: "Notification Settings" }],
    },
    {
      path: "/admin/public-assets",
      name: "Public Assets",
      permissions: ["admin_list_public_asset"],
      element: <PublicAssets />,
      layout: "portal",
      breadcrumb: [{ label: "Public Assets" }],
    },
    {
      // Super-admin read. No write verb exists on this endpoint at all — the
      // leftovers it lists are a RETENTION decision, and retention is
      // indefinite on every policy, so the page reports and does not act.
      path: "/admin/orphan-objects",
      name: "Orphaned Objects",
      permissions: ["admin_view_configuration"],
      element: <OrphanObjects />,
      layout: "portal",
      breadcrumb: [{ label: "Orphaned Objects" }],
    },
    {
      // Reads take admin_list_users, the CLEAR takes admin_assign_permission.
      // Gated on the read here on purpose: a reader who cannot clear should
      // still be able to see what is cached and why the app is serving stale
      // urls — the page surfaces the button's own refusal rather than hiding
      // the diagnosis behind the stronger permission.
      path: "/admin/redis-cache",
      name: "Redis Cache",
      permissions: ["admin_list_users"],
      element: <RedisCache />,
      layout: "portal",
      breadcrumb: [{ label: "Redis Cache" }],
    },
    {
      // Super admin only, same permission as the API's read. The WRITE verbs on
      // that endpoint take a second, stronger permission
      // (admin_manage_storage_objects) which the server enforces per action —
      // so a reader who is not a writer sees the page and gets refused on the
      // buttons, rather than the page lying about what they can do.
      path: "/admin/storage-objects",
      name: "Stored Objects",
      permissions: ["admin_view_configuration"],
      element: <StorageObjects />,
      layout: "portal",
      breadcrumb: [{ label: "Stored Objects" }],
    },
    {
      // Super admin only. The SAME permission the API gates on
      // (authMiddleware('Admin') + permissionMiddleware('admin_view_configuration')),
      // so the route, the sidebar entry and the endpoint cannot disagree —
      // e2e/tests/04-menu-route-parity.spec.ts asserts exactly that.
      path: "/admin/configuration",
      name: "Environment Configuration",
      permissions: ["admin_view_configuration"],
      element: <Configuration />,
      layout: "portal",
      breadcrumb: [{ label: "Environment Configuration" }],
    },
    {
      // Super admin only, the same gate as /api/admin/messaging-channels and
      // its /check. Read-only: the one button runs live credential checks on
      // the backend and sends nothing; changing a setting is a deploy.
      path: "/admin/messaging-channels",
      name: "Messaging channels",
      permissions: ["admin_view_configuration"],
      element: <MessagingChannels />,
      layout: "portal",
      breadcrumb: [{ label: "Messaging channels" }],
    },
  ],
};

const managementRoutes: RoutesProps = {
  path: "/management",
  name: "management",
  icon: "lock",
  element: <Navigate to="/management/approvals" />,
  children: [
    {
      path: "/management/approvals",
      name: "Approvals",
      permissions: ["agent_approval_get_list"],
      element: <Approvals />,
      layout: "portal",
      breadcrumb: [{ label: "Management", to: "/management" }, { label: "Approvals" }],
      children: [
       /* {
          path: "/management/approvals/add",
          name: "Add Car Model",
          permissions: ["add_car_models"],
          element: <ApprovalsAddEdit />,
        },
        {
          path: "/management/approvals/edit/:id",
          name: "Edit Car Model",
          permissions: ["update_car_models"],
          element: <ApprovalsAddEdit />,
        },*/
      ],
    },
    {
      path: "/management/requests",
      name: "Request",
      permissions: ["admin_get_list_request"],
      element: <Request />,
      layout: "portal",
      breadcrumb: [{ label: "Management", to: "/management" }, { label: "Requests" }],
      children: [
        {
          path: "/management/requests/:id",
          name: "Request Detail",
          permissions: ["admin_get_request_detail"],
          element: <RequestDetail />,
          layout: "portal",
          breadcrumb: [{ label: "Requests", to: "/management/requests" }, { label: "Request #RQ-<id> | Detail" }],
        },
      ],
    },
    {
      path: "/management/offers/:id",
      name: "Offer Detail",
      permissions: ["admin_manage_offers"],
      element: <OfferDetail />,
      layout: "portal",
      breadcrumb: [{ label: "Offers" }, { label: "Detail" }],
    },
    {
      path: "/management/customers/:id",
      name: "Customer Detail",
      permissions: ["see_customer_detail"],
      element: <CustomerDetail />,
      layout: "portal",
      breadcrumb: [{ label: "Customers", to: "/customers/list" }, { label: "Detail" }],
    },
    {
      // The old Documentation page: its sections are knowledge base articles now.
      path: "/management/documentation",
      name: "Documentation",
      permissions: ["admin_manage_knowledge_base"],
      element: <Navigate to="/content/knowledge-base" replace />,
      layout: "portal",
      breadcrumb: [{ label: "Knowledge base" }],
    },
  ]
};

const appRoutes = [authenticationRoutes];

// auth
const authRoutes: RoutesProps[] = [
  {
    path: "/auth/login",
    name: "Login",
    element: <Login />,
  },
  {
    path: "/auth/logout",
    name: "Logout",
    element: <Logout />,
  },
  {
    path: "/auth/forget-password",
    name: "Forget Password",
    element: <ForgetPassword />,
  },
  {
    path: "/auth/reset-password",
    name: "Reset Password",
    element: <ResetPasword />,
  },
];

// public routes
const otherPublicRoutes = [
  {
    path: "/maintenance",
    name: "Maintenance",
    element: <Maintenance />,
  },
  {
    path: "/error-404",
    name: "Error - 404",
    element: <Error404 />,
  },
  {
    path: "/error-500",
    name: "Error - 500",
    element: <Error500 />,
  },
  {
    path: "/portal-layout-preview",
    name: "PortalLayout Preview",
    element: <PortalLayoutPreview />,
  },
];
// flatten the list of all nested routes
const flattenRoutes = (routes: RoutesProps[]) => {
  let flatRoutes: RoutesProps[] = [];

  routes = routes || [];
  routes.forEach((item: RoutesProps) => {
    flatRoutes.push(item);

    if (typeof item.children !== "undefined") {
      flatRoutes = [...flatRoutes, ...flattenRoutes(item.children)];
    }
  });
  return flatRoutes;
};

// All routes
const authProtectedRoutes: any = [
  rootRoute,
  dashboardRoutes,
  ...appRoutes,
  parameterRoutes,
  agentRoutes,
  managementRoutes,
  customerRoutes/*
  extrapagesRoutes,
  uiRoutes,*/,
];
const publicRoutes = [...authRoutes, ...otherPublicRoutes];

const authProtectedFlattenRoutes = flattenRoutes([...authProtectedRoutes]);
const publicProtectedFlattenRoutes = flattenRoutes([...publicRoutes]);
export {
  publicRoutes,
  authProtectedRoutes,
  authProtectedFlattenRoutes,
  publicProtectedFlattenRoutes,
};
